import React, { useState } from 'react';
import { eventService } from '../services';
import gatheringService from '../services/gatheringService';
import { useDialog } from '../context';
import LinksEditor from './LinksEditor';
import PartyQuestionsSection from './PartyQuestionsSection';
import { PillButton, useToast, Toast } from './club/clubUi';
import { BigSwitch, kicker } from './gathering/gatheringUi';

/**
 * GatheringExtrasPanel — the "Extras" for a gathering, edited from its pass event's host edit
 * page (design_handoff_gatherings §5). House rules, pinned links, and party questions live on
 * the gathering's hidden pass event (no desync — the gathering→pass sync never touches these).
 * The Give/Take board switch is gathering-scoped with an optimistic revert. Co-hosts are
 * managed by the CoHostsPanel already rendered on the ops page.
 *
 * Props:
 *   passEvent    — the pass Event object (for initial house rules / links)
 *   gatheringId  — owning gathering (give/take toggle target)
 *   giveTakeEnabled — initial toggle value
 */
const MUTED = '#6B7280';
const RULE = '2px solid #F2F2F2';

export default function GatheringExtrasPanel({ passEvent, gatheringId, giveTakeEnabled: initialGiveTake = true }) {
  const { showAlert } = useDialog();
  const { toast, say } = useToast();
  const [houseRules, setHouseRules] = useState(
    (passEvent?.houseRules || []).map((r) => (typeof r === 'string' ? r : r.text)) || [],
  );
  const [links, setLinks] = useState(passEvent?.links || []);
  const [giveTake, setGiveTake] = useState(initialGiveTake !== false);
  const [saving, setSaving] = useState(false);

  const saveRulesAndLinks = async () => {
    setSaving(true);
    const filtered = houseRules.map((r) => (r || '').trim()).filter(Boolean);
    const res = await eventService.updateEvent(passEvent.id, { houseRules: filtered, links });
    setSaving(false);
    if (res.success) say('SAVED · EXTRAS UPDATED');
    else await showAlert({ title: 'COULD NOT SAVE', message: res.error || 'Could not save.', type: 'error' });
  };

  const toggleGiveTake = async () => {
    const next = !giveTake;
    setGiveTake(next);
    const res = await gatheringService.update(gatheringId, { giveTakeEnabled: next });
    if (!res.success) { setGiveTake(!next); await showAlert({ title: 'COULD NOT UPDATE', message: res.error?.message || 'Could not update.', type: 'error' }); }
  };

  const section = (title, children, right) => (
    <div className="flex flex-col" style={{ gap: 8, paddingTop: 14, borderTop: RULE }}>
      <span className="flex justify-between" style={{ gap: 8 }}>
        <span style={kicker(10)}>{title}</span>
        {right && <span style={{ ...kicker(9, '.14em'), color: '#9CA3AF' }}>{right}</span>}
      </span>
      {children}
    </div>
  );

  return (
    <section className="flex flex-col bg-white font-mono" style={{ gap: 18, padding: '22px 24px' }} data-testid="gathering-extras">
      <span className="flex flex-col" style={{ gap: 4 }}>
        <span style={kicker(12)}>GATHERING EXTRAS</span>
        <span style={{ fontSize: 11, lineHeight: 1.55, color: MUTED }}>House rules, links, the give/take board and event questions. Guests see these on the gathering page.</span>
      </span>

      <div className="flex flex-col" style={{ gap: 8 }}>
        <span style={kicker(10)}>HOUSE RULES</span>
        {houseRules.map((rule, i) => (
          <span key={i} className="flex items-center" style={{ gap: 8 }}>
            <span style={{ width: 22, flexShrink: 0, fontSize: 10, fontWeight: 700, color: '#9CA3AF', textAlign: 'right' }}>{i + 1}.</span>
            <input
              value={rule}
              placeholder="e.g. No shoes past the front door"
              aria-label={`House rule ${i + 1}`}
              onChange={(e) => setHouseRules((prev) => prev.map((r, idx) => (idx === i ? e.target.value : r)))}
              style={{ flex: 1, minWidth: 0, height: 42, padding: '0 16px', border: 'none', borderRadius: 999, background: '#F5F5F5', fontFamily: 'inherit', fontSize: 12, outline: 'none' }}
            />
            <button type="button" aria-label="Remove rule" onClick={() => setHouseRules((prev) => prev.filter((_, idx) => idx !== i))} style={{ width: 32, height: 32, flexShrink: 0, border: 'none', borderRadius: '50%', background: '#F2F2F2', color: '#DC2626', fontFamily: 'inherit', fontSize: 14, cursor: 'pointer' }}>×</button>
          </span>
        ))}
        <PillButton variant="tertiary" size={34} fontSize={9} style={{ alignSelf: 'flex-start' }} onClick={() => setHouseRules((prev) => [...prev, ''])}>+ ADD A RULE</PillButton>
      </div>

      {section('LINKS', <LinksEditor value={links} onChange={setLinks} theme="pill" />)}

      <PillButton size={46} onClick={saveRulesAndLinks} disabled={saving} data-testid="save-extras">{saving ? 'SAVING…' : 'SAVE RULES + LINKS'}</PillButton>

      <span className="flex items-center justify-between" style={{ gap: 12, paddingTop: 14, borderTop: RULE }}>
        <span className="flex flex-col" style={{ gap: 4 }}>
          <span style={kicker(10)}>GIVE / TAKE BOARD</span>
          <span style={{ fontSize: 11, lineHeight: 1.5, color: MUTED }}>Guests post offers and asks on the gathering page.</span>
        </span>
        <BigSwitch on={giveTake} onChange={toggleGiveTake} label="Give / take board" testId="give-take-switch" />
      </span>

      {section('EVENT QUESTIONS', <PartyQuestionsSection eventId={passEvent.id} isHost manage variant="portal" />, 'ASKED AT CHECKOUT')}
      <Toast text={toast} />
    </section>
  );
}
